import React, { useEffect, useState, ReactNode } from 'react'
import MobileRestriction from './MobileRestriction'

interface MobileDetectionWrapperProps {
  children: ReactNode
  disabled?: boolean
}

type DeviceType = 'mobile' | 'tablet' | 'valid_device'

// Phone-sized viewport. Uses the smaller of innerWidth/innerHeight so a phone is
// detected in both portrait and landscape, while tablets and desktops are not.
// Avoids window.screen (the physical monitor) and touch, which are unreliable in dev tools.
export const isMobileDevice = (): boolean => Math.min(window.innerWidth, window.innerHeight) < 768

// Survey routes a phone is allowed to reach and complete (every survey except the pre-survey).
const MOBILE_ALLOWED_SURVEY_PATHS = ['/postsurvey', '/90postsurvey']

export const isMobileAllowedSurveyRedirect = (path: string | null | undefined): boolean => {
  if (path == null || path === '') return false
  const pathname = (path.split('?')[0].split('#')[0].replace(/\/+$/, '')) || '/'
  return MOBILE_ALLOWED_SURVEY_PATHS.includes(pathname)
}

export const MobileDetectionWrapper: React.FC<MobileDetectionWrapperProps> = ({
  children,
  disabled = false
}) => {
  const [deviceType, setDeviceType] = useState<DeviceType>('valid_device')

  // Function for device detection (moved outside useEffect for reuse)
  const detectDeviceType = (): DeviceType => {
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const isSmallWindowSize = window.innerWidth < 1024 || window.innerHeight < 768
    const isSmallScreen = window.screen.width < 768 || window.screen.height < 768

    if (isMobileDevice()) {
      return 'mobile'
    }

    // Tablet detection - any device with touch that's not a small screen or has touch and it's window size is to small
    if ((hasTouch && !isSmallScreen) || (hasTouch && isSmallWindowSize)) {
      return 'tablet'
    }

    return 'valid_device'
  }

  useEffect(() => {
    // Initial detection
    const initialType = detectDeviceType()
    setDeviceType(initialType)

    // Handle resize events
    const handleResize = () => {
      const currentType = detectDeviceType()
      // Only update state if the device type changes
      if (currentType !== deviceType) {
        setDeviceType(currentType)
      }
    }

    // Add resize listener
    window.addEventListener('resize', handleResize)

    // Clean up listener
    return () => window.removeEventListener('resize', handleResize)
  }, [deviceType])

  const shouldShowRestriction = deviceType === 'mobile' && !disabled

  if (shouldShowRestriction) {
    return <MobileRestriction />
  }

  return <>{children}</>
}
